import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { C } from '../styles/tokens.js';

const SEEN_KEY = 'pium-collab:help-seen';

const SECTIONS = [
  { id: 'welcome', label: '👋 처음 오셨나요?', icon: '👋' },
  { id: 'quickstart', label: '🚀 5분 빠른 시작', icon: '🚀' },
  { id: 'tabs', label: '📑 6개 탭 둘러보기', icon: '📑' },
  { id: 'whiteboard', label: '🗂 화이트보드 상세', icon: '🗂' },
  { id: 'cards', label: '🃏 카드 사용법', icon: '🃏' },
  { id: 'project', label: '📂 프로젝트 관리', icon: '📂' },
  { id: 'collab', label: '👥 협업 (채팅·검색)', icon: '👥' },
  { id: 'shortcuts', label: '⌨ 키보드 단축키', icon: '⌨' },
  { id: 'faq', label: '❓ 자주 묻는 질문', icon: '❓' },
];

const KBD = ({ children }) => (
  <kbd style={{
    display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 700,
    background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 4,
    color: C.text, fontFamily: 'monospace', margin: '0 2px',
    boxShadow: `0 1px 0 ${C.border}`,
  }}>{children}</kbd>
);

const Step = ({ n, title, children }) => (
  <div style={{ display: 'flex', gap: 14, marginBottom: 18 }}>
    <div style={{ width: 28, height: 28, borderRadius: '50%', background: C.goldBg, color: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0, border: `1.5px solid ${C.gold}66` }}>{n}</div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.7 }}>{children}</div>
    </div>
  </div>
);

const Tip = ({ children }) => (
  <div style={{ padding: '10px 14px', background: C.goldBg, border: `1px solid ${C.gold}44`, borderLeft: `3px solid ${C.gold}`, borderRadius: 4, fontSize: 12, color: C.text, lineHeight: 1.6, margin: '12px 0' }}>
    💡 <strong style={{ color: C.gold }}>팁:</strong> {children}
  </div>
);

const Note = ({ children }) => (
  <div style={{ padding: '10px 14px', background: C.blueBg, border: `1px solid ${C.blue}44`, borderLeft: `3px solid ${C.blue}`, borderRadius: 4, fontSize: 12, color: C.text, lineHeight: 1.6, margin: '12px 0' }}>
    ℹ️ {children}
  </div>
);

const H2 = ({ children }) => <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, marginTop: 0, marginBottom: 14, letterSpacing: -0.3 }}>{children}</h2>;
const H3 = ({ children }) => <h3 style={{ fontSize: 15, fontWeight: 800, color: C.text, marginTop: 24, marginBottom: 10 }}>{children}</h3>;
const P = ({ children }) => <p style={{ fontSize: 13, color: C.textDim, lineHeight: 1.7, margin: '0 0 10px 0' }}>{children}</p>;

export default function HelpModal({ open, onClose, autoOpened = false }) {
  const [section, setSection] = useState('welcome');
  const [dontShow, setDontShow] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [section]);

  const handleClose = () => {
    // 닫으면 무조건 SEEN 설정 (자동 표시는 1회만)
    try { localStorage.setItem(SEEN_KEY, '1'); } catch {}
    onClose();
  };

  if (!open) return null;

  return createPortal(
    <div onClick={handleClose} onMouseDown={e => e.stopPropagation()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99998, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}
        style={{ background: C.surface, borderRadius: 14, width: '100%', maxWidth: 1100, height: '90vh', maxHeight: 760, display: 'flex', border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.6)' }}>

        {/* 좌측 목차 */}
        <div style={{ width: 240, background: C.surface2, borderRight: `1px solid ${C.border}`, padding: 14, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '8px 12px 16px', borderBottom: `1px solid ${C.border}`, marginBottom: 10 }}>
            <div style={{ fontSize: 17, fontWeight: 900, color: C.gold, letterSpacing: 1 }}>📖 사용방법</div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>PIUM 협업 보드 가이드</div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {SECTIONS.map(s => (
              <div key={s.id} onClick={() => setSection(s.id)}
                style={{ padding: '10px 12px', borderRadius: 6, cursor: 'pointer', marginBottom: 2,
                  background: section === s.id ? C.goldBg : 'transparent',
                  color: section === s.id ? C.gold : C.text,
                  fontSize: 12, fontWeight: section === s.id ? 700 : 600 }}>
                {s.label}
              </div>
            ))}
          </div>
          <div style={{ padding: '12px 0 0', borderTop: `1px solid ${C.border}`, marginTop: 8 }}>
            {autoOpened && (
              <div style={{ fontSize: 10, color: C.dim, marginBottom: 8, padding: '6px 8px', background: C.surface3, borderRadius: 4, lineHeight: 1.5 }}>
                💡 다시 보려면 우상단 <strong style={{ color: C.gold }}>❓</strong> 버튼 클릭
              </div>
            )}
            <button onClick={handleClose}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: 'none', background: C.gold, color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              시작하기
            </button>
          </div>
        </div>

        {/* 우측 콘텐츠 */}
        <div ref={contentRef} style={{ flex: 1, overflowY: 'auto', padding: '32px 40px', position: 'relative' }}>
          <button onClick={handleClose} style={{ position: 'absolute', top: 16, right: 20, background: 'transparent', border: 'none', color: C.dim, fontSize: 24, cursor: 'pointer', padding: '4px 8px' }}>×</button>

          {section === 'welcome' && (
            <div>
              <H2>👋 PIUM 협업 보드에 오신 것을 환영합니다</H2>
              <P>
                <strong style={{ color: C.text }}>PIUM 협업 보드</strong>는 전시 제안서를 한 곳에서 기획·검토·발송까지 끌고 가는 팀 도구입니다.
                흩어진 PPT·PDF·아이디어 메모·수정 요청·일정·파일을 <strong style={{ color: C.gold }}>하나의 화면</strong>에서 다룹니다.
              </P>
              <H3>이 도구로 무엇을 하나요?</H3>
              <ul style={{ fontSize: 13, color: C.textDim, lineHeight: 1.9, paddingLeft: 20 }}>
                <li>📌 화이트보드에 <strong style={{ color: C.text }}>아이디어 메모·수정 요청·파일 링크</strong>를 자유롭게 배치</li>
                <li>📅 제안서 마감일을 <strong style={{ color: C.text }}>D-Day 카드</strong>로 화이트보드에 두고 한눈에 추적</li>
                <li>📁 PPT/PDF 파일을 <strong style={{ color: C.text }}>버전별로 관리</strong>하고 최종본만 골라 납품 폴더로 자동 복사</li>
                <li>✏ 수정 요청을 <strong style={{ color: C.text }}>페이지 번호와 함께 기록</strong>해서 원고 페이지로 바로 점프</li>
                <li>💬 팀원과 <strong style={{ color: C.text }}>채팅</strong>으로 즉시 소통, 모든 변경이 변경로그에 자동 기록</li>
              </ul>
              <Tip>처음이시라면 좌측 메뉴의 <strong>🚀 5분 빠른 시작</strong>부터 따라 해보세요.</Tip>
            </div>
          )}

          {section === 'quickstart' && (
            <div>
              <H2>🚀 5분 빠른 시작</H2>
              <P>처음 사용자가 5분 안에 핵심 기능을 익힐 수 있는 가이드입니다.</P>
              <Step n="1" title="프로젝트 선택 또는 생성">
                화면 상단 좌측에 <strong>제안 프로젝트 탭</strong>이 있습니다. 기본으로 "전북특별자치도교육청과학교육원 제안서"가 만들어져 있어요.
                <br/><br/>
                새 프로젝트는 탭 옆 <strong style={{ color: C.gold }}>+ 버튼</strong>을 누르고 이름을 입력하세요. (예: "국립과천과학관 제안")
              </Step>
              <Step n="2" title="화이트보드에 첫 메모 추가">
                상단 좌측 <strong>🗂 화이트보드</strong> 탭으로 이동 → 툴바의 <strong style={{ color: C.gold }}>+ 메모</strong> 버튼 클릭
                <br/>화면 가운데 노란 메모 카드가 나타납니다.
                <br/><br/>
                카드 우상단 <strong>✏ 편집</strong> 아이콘(또는 카드 더블클릭)으로 제목·내용·색상을 바꾸세요.
              </Step>
              <Step n="3" title="제안서 마감일 추가">
                툴바의 <strong style={{ color: C.red }}>📅 마감일</strong> 버튼 → 빨간 D-Day 카드 생성
                <br/>더블클릭으로 라벨(예: "1차 시안 제출")과 날짜를 변경하면 D-N 카운트다운이 자동 표시됩니다.
                <br/><br/>
                <Note>D-3 이하면 자동으로 빨간색 강조, 지난 일자는 회색 D+N으로 표시.</Note>
              </Step>
              <Step n="4" title="파일 업로드와 화이트보드 연결">
                <strong>📁 파일</strong> 탭으로 이동 → 상단 점선 박스를 <strong>클릭</strong>하거나 파일을 <strong>드래그</strong>해서 업로드
                <br/>업로드한 파일 옆 <strong style={{ color: C.gold }}>🖼 보드로</strong> 버튼을 누르면 화이트보드에 카드로 자동 추가됩니다.
                <br/><br/>
                PDF 파일이면 카드 안에서 <strong>👁 미리보기 ON</strong> 토글로 첫 페이지를 바로 확인할 수 있어요.
              </Step>
              <Step n="5" title="팀원과 채팅 소통">
                헤더 우측 <strong style={{ color: C.gold }}>💬 채팅 버튼</strong> 클릭 → 우측에 팀 채팅 패널이 열립니다.
                <br/>발신자를 본인으로 바꾸고 메시지를 입력하세요. 채팅 내역은 자동으로 저장됩니다.
              </Step>
              <Tip>5단계만 익히면 일상 작업의 80%를 처리할 수 있습니다. 나머지 기능은 필요할 때 천천히 익히세요.</Tip>
            </div>
          )}

          {section === 'tabs' && (
            <div>
              <H2>📑 6개 탭 둘러보기</H2>
              <P>화면 상단 탭은 제안서 작업 흐름을 6단계로 나누어 보여줍니다. 좌→우 순서대로 진행하시면 자연스러워요.</P>
              {[
                { icon: '🗂', name: '화이트보드', desc: '아이디어·메모·수정요청·파일링크·마감일을 자유롭게 배치하는 무한 캔버스. 펜으로 직접 그리거나 영역으로 묶을 수 있어요.', tip: '가장 많이 쓰는 탭. 매일 열어두세요.' },
                { icon: '📁', name: '파일', desc: 'PPT/PDF/DWG/XLSX 등 모든 제안서 관련 파일을 버전별로 관리. FINAL 표시·재업로드·PDF 뷰어·PPT 최적화 워크플로우.', tip: '드래그앤드롭 또는 클릭으로 즉시 업로드.' },
                { icon: '✏️', name: '수정사항', desc: '"p.12 메인 콘셉트 수정", "p.21 이미지 교체" 같은 페이지별 수정 요청을 카드로 추적. ↗ 버튼으로 SharePoint 원고로 바로 점프.', tip: '상단 카운터로 진행중/대기/완료 한눈에.' },
                { icon: '📅', name: '일정', desc: '마일스톤 타임라인 + 다음 마일스톤 카드(NEXT) + 모든 프로젝트의 마감일 통합 위젯.', tip: 'D-Day는 화이트보드와 자동 연동.' },
                { icon: '📦', name: '아카이브', desc: 'FINAL 표시된 파일들을 체크해서 납품용 폴더로 일괄 복사. 버전·파일명·경로 모두 보존.', tip: '제안서 발송 직전에 사용.' },
                { icon: '📋', name: '변경 로그', desc: '모든 사용자의 모든 액션을 시간순으로 자동 기록. 사용자별·타입별 필터 + 사이드바 통계.', tip: '"누가 언제 무엇을 했는지" 추적용.' },
              ].map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 16, padding: '14px 16px', background: C.surface2, borderRadius: 8, marginBottom: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 28, lineHeight: 1 }}>{t.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 4 }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: C.textDim, lineHeight: 1.6, marginBottom: 6 }}>{t.desc}</div>
                    <div style={{ fontSize: 11, color: C.gold, fontWeight: 600 }}>💡 {t.tip}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {section === 'whiteboard' && (
            <div>
              <H2>🗂 화이트보드 상세</H2>
              <P>5000×3500 가상 캔버스 위에 카드·영역·마감일을 자유롭게 배치하는 작업 공간입니다.</P>

              <H3>🛠 툴바 (좌상단)</H3>
              <ul style={{ fontSize: 13, color: C.textDim, lineHeight: 2, paddingLeft: 20 }}>
                <li><strong>🖱 선택</strong> / <strong>✏ 펜</strong>: 모드 전환 (펜은 자유 드로잉)</li>
                <li><strong>⬚ 영역</strong>: 카드들을 묶는 박스 (점선 사각형)</li>
                <li><strong>+ 메모 / + 수정 / + 파일 / + 미디어</strong>: 4가지 카드 생성</li>
                <li><strong>📅 마감일</strong>: D-Day 카드 생성</li>
                <li><strong>↶ 되돌리기 / ↷ 다시 실행</strong>: 최대 50단계</li>
                <li><strong>+ / 100% / −</strong>: 줌 (20% ~ 200%)</li>
              </ul>

              <H3>🗺 미니맵 (우하단)</H3>
              <P>전체 캔버스의 축소도를 보여줍니다. <strong>현재 보이는 영역</strong>이 골드 사각형으로 표시되고, 미니맵 안의 <strong>아무 곳이나 클릭</strong>하면 그 위치로 이동합니다.</P>

              <H3>🧭 캔버스 이동 방법</H3>
              <ul style={{ fontSize: 13, color: C.textDim, lineHeight: 2, paddingLeft: 20 }}>
                <li>🖱 <strong>마우스 스크롤</strong>: 위/아래/좌/우 스크롤로 캔버스 이동</li>
                <li>⌨ <KBD>Space</KBD> 누른 채 <strong>드래그</strong>: 부드러운 패닝 (구글맵처럼)</li>
                <li>⌨ <KBD>Ctrl</KBD> + <strong>마우스 휠</strong>: 줌 인/아웃</li>
                <li>🗺 <strong>미니맵 클릭</strong>: 원하는 위치로 워프</li>
              </ul>

              <H3>⬚ 영역 박스</H3>
              <P>여러 카드를 시각적으로 묶고 싶을 때 사용. 영역 위에서 <strong>더블클릭</strong>하면 이름·색상·크기를 편집할 수 있고, 우하단 <strong>↘ 핸들</strong>을 드래그해서 크기를 조정합니다.</P>

              <H3>📅 마감일 카드</H3>
              <P>날짜를 설정하면 자동으로 D-Day가 계산됩니다. <strong>D-3 이하</strong>는 빨간색으로 변하고, <strong>지난 일자</strong>는 회색 D+N으로 표시됩니다. 일정 탭의 "다가오는 마감일" 위젯에도 자동 연동됩니다.</P>

              <H3>✏ 펜 도구</H3>
              <P>모드를 <strong>✏ 펜</strong>으로 바꾸면 자유 드로잉이 가능합니다. 색상 6종, 굵기 1~10 조정 가능, "전체 지우기"로 일괄 삭제.</P>
              <Note>드로잉 중에는 카드 클릭이 비활성화됩니다. 다시 카드 작업하려면 🖱 선택 모드로 돌아오세요.</Note>
            </div>
          )}

          {section === 'cards' && (
            <div>
              <H2>🃏 카드 사용법</H2>
              <P>화이트보드의 모든 요소는 "카드" 형태입니다. 4가지 타입이 있어요.</P>

              <H3>📝 메모 카드 (골드)</H3>
              <P>자유로운 아이디어, 콘셉트, 메모를 적습니다. 가장 많이 쓰는 기본 카드.</P>

              <H3>✅ 수정 카드 (블루)</H3>
              <P>"누가, 언제까지, 무엇을 수정" 같은 작업 추적용. 수정사항 탭의 데이터와는 별개로 화이트보드 위에 시각적으로 펼쳐두는 용도.</P>

              <H3>📄 파일 카드 (그린)</H3>
              <P>화이트보드 위에 파일 링크를 두는 카드입니다.</P>
              <ul style={{ fontSize: 13, color: C.textDim, lineHeight: 1.9, paddingLeft: 20 }}>
                <li><strong>💻 PC에서</strong>: 본인 컴퓨터의 파일을 직접 첨부</li>
                <li><strong>📁 파일탭에서</strong>: 이미 파일 탭에 업로드된 파일 선택</li>
                <li>첨부 후 <strong>👁 미리보기 ON</strong>: PDF는 첫 페이지를 카드 안에서 즉시 표시</li>
                <li>카드 ↘ 핸들로 크기 조정하면 미리보기도 따라 커집니다</li>
              </ul>

              <H3>🎬 미디어 카드 (오렌지)</H3>
              <P>비디오·이미지·오디오를 카드 안에서 직접 재생.</P>
              <ul style={{ fontSize: 13, color: C.textDim, lineHeight: 1.9, paddingLeft: 20 }}>
                <li>지원 비디오: mp4, webm, mov</li>
                <li>지원 이미지: jpg, png, gif, webp, svg</li>
                <li>지원 오디오: mp3, wav, m4a</li>
                <li>첨부 즉시 자동 재생 컨트롤 표시 (▶ 재생 ON 토글)</li>
              </ul>

              <H3>📌 모든 카드의 공통 동작</H3>
              <ul style={{ fontSize: 13, color: C.textDim, lineHeight: 2, paddingLeft: 20 }}>
                <li>🖱 <strong>드래그</strong>: 위치 이동</li>
                <li>↘ <strong>핸들 드래그</strong>: 크기 조정</li>
                <li><strong>✏ 버튼</strong> 또는 <strong>더블클릭</strong>: 제목·내용·색상 편집</li>
                <li><strong>× 버튼</strong>: 카드 삭제</li>
                <li>😊 <strong>이모지</strong>: 8종 반응 추가 (👍 ✅ ❗ 🔥 등)</li>
                <li>💬 <strong>댓글</strong>: 카드별 댓글 스레드</li>
              </ul>

              <Tip>실수로 삭제했다면 <KBD>Ctrl</KBD>+<KBD>Z</KBD>로 즉시 복구!</Tip>
            </div>
          )}

          {section === 'project' && (
            <div>
              <H2>📂 프로젝트 관리</H2>
              <P>여러 제안 건을 동시에 진행할 때 사용하는 멀티 프로젝트 시스템입니다.</P>

              <H3>📑 멀티 탭 (최대 4개 동시 표시)</H3>
              <P>헤더 상단에 활성 프로젝트들이 탭 형식으로 나열됩니다. 클릭으로 즉시 전환되며, 각 프로젝트는 독립된 화이트보드·파일·일정을 가집니다.</P>
              <ul style={{ fontSize: 13, color: C.textDim, lineHeight: 1.9, paddingLeft: 20 }}>
                <li><strong>+ 버튼</strong>: 새 프로젝트 생성 (이름 입력 prompt)</li>
                <li><strong>활성 탭 더블클릭</strong>: 이름 변경 (인라인 편집)</li>
                <li><strong>활성 탭 × 버튼</strong>: 프로젝트 삭제 (확인 모달)</li>
                <li>5개 이상이면 <strong>⋯ +N</strong>으로 접힘, ⋮ 메뉴에서 모두 보기</li>
              </ul>

              <H3>🔍 검색 + 백업 (⋮ 메뉴)</H3>
              <P>헤더 ⋮ 버튼을 누르면 모든 프로젝트 검색 + 일괄 백업 메뉴가 열립니다.</P>
              <ul style={{ fontSize: 13, color: C.textDim, lineHeight: 1.9, paddingLeft: 20 }}>
                <li><strong>🔍 검색</strong>: 프로젝트 이름·카드 내용·마감일 라벨 통합 검색</li>
                <li><strong>⎘ 복제</strong>: 현재 프로젝트의 모든 상태를 그대로 복사 (참고용 변형 만들 때)</li>
                <li><strong>⬇ 개별 백업</strong>: 이 프로젝트만 JSON 다운로드</li>
                <li><strong>⬇ 전체 백업</strong>: 모든 프로젝트를 단일 JSON으로</li>
                <li><strong>⬆ 불러오기</strong>: JSON 파일로 복원 (ID 충돌 자동 처리)</li>
              </ul>

              <H3>💾 자동 저장</H3>
              <Note>모든 변경은 브라우저 localStorage에 즉시 자동 저장됩니다. 새로고침해도 데이터 유지. <strong>주기적으로 ⬇ 전체 백업</strong>으로 JSON을 받아두세요 - 브라우저 데이터 손실 대비.</Note>

              <H3>🌐 글로벌 검색 (헤더 🔍)</H3>
              <P>헤더 우측 <strong>🔍 전체 검색</strong> 입력란은 모든 프로젝트의 카드 제목·내용·마감일까지 검색합니다. 결과를 클릭하면 해당 프로젝트로 즉시 이동.</P>
            </div>
          )}

          {section === 'collab' && (
            <div>
              <H2>👥 협업 (채팅·검색·로그)</H2>

              <H3>💬 팀 채팅</H3>
              <P>헤더 우측 <strong>💬 버튼</strong>을 누르면 우측에 채팅 패널이 열립니다.</P>
              <ul style={{ fontSize: 13, color: C.textDim, lineHeight: 1.9, paddingLeft: 20 }}>
                <li><strong>발신자 선택</strong>: 5명(윤대표/이팀장/박대리/김과장/최주임) 중 본인을 선택</li>
                <li><strong>본인 메시지</strong>: 골드 배경, 우측 정렬</li>
                <li><strong>타인 메시지</strong>: 회색 배경, 좌측 정렬 + 컬러 아바타</li>
                <li><strong>Enter</strong>: 즉시 전송</li>
                <li><strong>🗑 비우기</strong>: 대화 내역 전체 삭제</li>
              </ul>

              <H3>👤 사용자 아바타</H3>
              <P>헤더 우측 끝에 팀원 컬러 아바타가 표시됩니다 (이팀장 골드, 박대리 블루, 김과장 그린 등). 같은 색상이 카드의 작성자·수정 요청자·로그·채팅에서 일관되게 사용됩니다.</P>

              <H3>📋 변경 로그</H3>
              <P>모든 사용자의 모든 액션이 자동 기록됩니다. <strong>📋 변경 로그</strong> 탭에서 확인:</P>
              <ul style={{ fontSize: 13, color: C.textDim, lineHeight: 1.9, paddingLeft: 20 }}>
                <li><strong>좌측 사이드바</strong>: 사용자별 활동 카운트 + 액션 타입 분포 차트</li>
                <li><strong>사용자 클릭</strong>: 그 사람 로그만 필터</li>
                <li><strong>상단 칩</strong>: 화이트보드/파일/수정/댓글/아카이브/최적화 타입별 필터</li>
                <li>시각·사용자·타입·메시지가 한 줄로 정렬</li>
              </ul>

              <Tip>"오늘 누가 무엇을 했나" 궁금하면 변경로그 탭 + 사용자 사이드바를 클릭하면 1초만에 확인 가능.</Tip>
            </div>
          )}

          {section === 'shortcuts' && (
            <div>
              <H2>⌨ 키보드 단축키</H2>
              <P>마우스보다 빠른 키보드 단축키 모음입니다.</P>

              <H3>전역 단축키</H3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 20 }}>
                <tbody>
                  {[
                    [<><KBD>Ctrl</KBD>+<KBD>Z</KBD></>, '되돌리기 (Undo)'],
                    [<><KBD>Ctrl</KBD>+<KBD>Y</KBD></>, '다시 실행 (Redo)'],
                    [<><KBD>Ctrl</KBD>+<KBD>Shift</KBD>+<KBD>Z</KBD></>, '다시 실행 (Redo, 대체)'],
                  ].map((row, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '10px 12px', width: 220 }}>{row[0]}</td>
                      <td style={{ padding: '10px 12px', color: C.textDim }}>{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <H3>화이트보드 전용</H3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 20 }}>
                <tbody>
                  {[
                    [<><KBD>Space</KBD> + 드래그</>, '캔버스 패닝 (구글맵 스타일)'],
                    [<><KBD>Ctrl</KBD> + 마우스 휠</>, '확대 / 축소'],
                    [<>마우스 휠</>, '캔버스 위/아래/좌/우 스크롤'],
                    [<>카드 더블클릭</>, '카드 편집 팝업'],
                    [<>영역 박스 더블클릭</>, '영역 편집 팝업'],
                    [<>마감일 더블클릭</>, '날짜 편집 팝업'],
                  ].map((row, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '10px 12px', width: 220 }}>{row[0]}</td>
                      <td style={{ padding: '10px 12px', color: C.textDim }}>{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <H3>입력란/채팅에서</H3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {[
                    [<><KBD>Enter</KBD></>, '메시지/댓글 전송, 편집 저장'],
                    [<><KBD>Esc</KBD></>, '편집 취소'],
                  ].map((row, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '10px 12px', width: 220 }}>{row[0]}</td>
                      <td style={{ padding: '10px 12px', color: C.textDim }}>{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Tip><KBD>Ctrl</KBD>+<KBD>Z</KBD>는 입력란 안이 아닐 때만 작동합니다. 입력란에서는 텍스트 자체의 undo가 우선.</Tip>
            </div>
          )}

          {section === 'faq' && (
            <div>
              <H2>❓ 자주 묻는 질문</H2>

              {[
                {
                  q: 'Q. 화면이 갑자기 빈 모눈종이만 보입니다.',
                  a: '예기치 못한 오류가 발생한 경우입니다. 자동으로 복구 화면이 뜨면 "🔄 새로고침" 또는 "✏ 펜 스트로크만 초기화" 버튼을 누르세요. 둘 다 안 되면 "🗑 전체 데이터 초기화"로 처음부터 시작 (단, 백업한 JSON이 있어야 복원 가능).'
                },
                {
                  q: 'Q. 데이터는 어디에 저장되나요?',
                  a: '브라우저 localStorage에 자동 저장됩니다. 같은 브라우저·같은 도메인이면 새로고침해도 그대로 유지. 다른 PC에서 보려면 ⬇ 전체 백업 → 그 PC에서 ⬆ 불러오기.'
                },
                {
                  q: 'Q. 팀원과 실시간으로 같이 편집할 수 있나요?',
                  a: '현재 버전은 개별 브라우저 저장 방식입니다. 같은 화이트보드를 같이 보려면 백업 JSON을 공유하거나 화면 공유로 작업하세요. 실시간 협업은 향후 SharePoint 연동에서 지원 예정.'
                },
                {
                  q: 'Q. PDF 미리보기가 안 됩니다.',
                  a: 'PDF는 PC에서 직접 업로드한 파일만 미리보기됩니다. 파일 탭에서 가져온 파일은 SharePoint URL이 연결되어야 미리보기 가능 (현재는 URL 미설정 → "URL 없음" 표시).'
                },
                {
                  q: 'Q. PPTX 미리보기는 어떻게 하나요?',
                  a: 'PPTX는 브라우저에서 직접 렌더가 어려워 SharePoint Online Office Viewer를 사용합니다. SharePoint에 업로드된 파일이어야 하며, 향후 PIUM HQ SharePoint 연동 시 자동 작동합니다.'
                },
                {
                  q: 'Q. 캔버스가 좁아 보여요. 더 넓게 쓸 수 있나요?',
                  a: '캔버스는 5000×3500 가상 영역입니다. 화면에 보이는 건 일부일 뿐, 마우스 스크롤이나 Space+드래그로 모든 영역을 사용할 수 있습니다. 미니맵으로 위치를 확인하세요.'
                },
                {
                  q: 'Q. 다크/라이트 테마는 어떻게 바꾸나요?',
                  a: '헤더 우측의 ☀ 또는 🌙 버튼을 누르면 즉시 전환됩니다. 선택한 테마는 브라우저에 저장되어 다음 방문 시에도 유지됩니다.'
                },
                {
                  q: 'Q. 파일 첨부는 어디에 저장되나요?',
                  a: '현재는 브라우저 메모리(blob)에만 저장됩니다. 새로고침 시 첨부된 파일은 사라지므로, 중요한 파일은 파일 탭에 따로 업로드해두세요. 영구 저장은 향후 SharePoint 통합 시 자동 처리됩니다.'
                },
              ].map((item, i) => (
                <div key={i} style={{ marginBottom: 18, padding: '14px 16px', background: C.surface2, borderRadius: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.gold, marginBottom: 8 }}>{item.q}</div>
                  <div style={{ fontSize: 12, color: C.textDim, lineHeight: 1.7 }}>{item.a}</div>
                </div>
              ))}

              <H3>도움이 더 필요하시면</H3>
              <P>운영팀(DOX)에게 카카오워크 또는 팀 채팅으로 문의해주세요. 버그 발견 시 변경로그 탭의 마지막 액션과 함께 알려주시면 더 빠르게 해결됩니다.</P>
            </div>
          )}

        </div>
      </div>
    </div>,
    document.body
  );
}

export { SEEN_KEY };
